import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import type { HistoryState } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { PatrolMap } from "@/components/PatrolMap";
import { SiteScopeSheet, type ScopeCategory } from "@/components/dashboards/SiteScopeSheet";
import { LiveOpsStrip } from "@/components/dashboards/LiveOpsStrip";
import { useLiveSiteOps } from "@/lib/dashboard/use-live-site-ops";
import { fromDashboard } from "@/lib/dashboard-origin";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Building2, Users, Briefcase, AlertTriangle, ShieldCheck, ShieldAlert,
  Bell, Repeat, ChevronRight, MapPin, QrCode, FileText, Mail, ClipboardList, Hexagon,
} from "lucide-react";

const ALLOWED = ["org_owner", "admin", "dispatcher"];

export const Route = createFileRoute("/_authenticated/manager")({
  component: ManagerDashboard,
  head: () => ({ meta: [{ title: "Manager Dashboard — MissionReady" }] }),
});

type Site = { id: string; name: string; latitude: number | null; longitude: number | null };
type Counts = {
  sites: number; onDuty: number; openShifts: number; alerts: number;
  onSite: number; offPerimeter: number; openAlerts: number; shiftRequests: number;
};

function ManagerDashboard() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const allowed = isPlatformAdmin || (currentRole && ALLOWED.includes(currentRole));
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState<string>("all");
  const [scope, setScope] = useState<ScopeCategory | null>(null);
  const [c, setC] = useState<Counts>({
    sites: 0, onDuty: 0, openShifts: 0, alerts: 0,
    onSite: 0, offPerimeter: 0, openAlerts: 0, shiftRequests: 0,
  });

  useEffect(() => {
    if (!currentOrg) return;
    supabase.from("sites").select("id,name,latitude,longitude")
      .eq("organization_id", currentOrg.id).eq("active", true)
      .then(({ data }) => setSites((data ?? []) as Site[]));
  }, [currentOrg?.id]);

  useEffect(() => {
    if (!currentOrg) return;
    const orgId = currentOrg.id;
    const filterSite = (q: any) => (siteId === "all" ? q : q.eq("site_id", siteId));
    (async () => {
      const [sitesQ, shifts, openShifts, incs, perim, alerts, requests] = await Promise.all([
        supabase.from("sites").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("active", true),
        filterSite(supabase.from("shifts").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("status", "active")),
        filterSite(supabase.from("shifts").select("*", { count: "exact", head: true }).eq("organization_id", orgId).is("officer_id", null).eq("status", "scheduled")),
        filterSite(supabase.from("incidents").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("status", ["open", "in_progress"])),
        supabase.from("geofence_events").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("event_type", "exit").gte("occurred_at", new Date(Date.now() - 60 * 60 * 1000).toISOString()),
        supabase.from("incidents").select("*", { count: "exact", head: true }).eq("organization_id", orgId).eq("status", "open"),
        supabase.from("dispatch_assignments").select("*", { count: "exact", head: true }).eq("organization_id", orgId).in("status", ["pending"]),
      ]);
      setC({
        sites: sitesQ.count ?? 0,
        onDuty: shifts.count ?? 0,
        openShifts: openShifts.count ?? 0,
        alerts: incs.count ?? 0,
        onSite: shifts.count ?? 0,
        offPerimeter: perim.count ?? 0,
        openAlerts: alerts.count ?? 0,
        shiftRequests: requests.count ?? 0,
      });
    })();
  }, [currentOrg?.id, siteId]);

  // Single-site organizations scope directly to their only site.
  useEffect(() => {
    if (sites.length === 1 && siteId === "all") setSiteId(sites[0].id);
  }, [sites, siteId]);

  const visibleSiteIds = useMemo(
    () => (siteId === "all" ? sites.map((s) => s.id) : [siteId]),
    [sites, siteId],
  );
  const scopeLabel =
    sites.length === 1
      ? sites[0]?.name ?? "Site"
      : siteId === "all"
        ? "All sites"
        : sites.find((s) => s.id === siteId)?.name ?? "Selected site";

  const live = useLiveSiteOps({ orgId: currentOrg?.id, sites, siteIds: visibleSiteIds });
  const originState = fromDashboard({ id: "manager", siteId, scopeLabel });

  if (!allowed) {
    return (
      <div className="px-4 py-10 text-center text-sm text-muted-foreground">
        You don't have access to the Manager Dashboard.
      </div>
    );
  }

  return (
    <div className="px-4 py-5 md:px-6 md:py-7">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="font-display text-2xl font-bold md:text-3xl">Manager Dashboard</h1>
        </div>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Command Center · Real-Time Overview</p>
      </div>

      {/* Site filter — hidden when the organization only has one site */}
      {sites.length > 1 ? (
        <div className="mb-5 rounded-2xl border border-border bg-card px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Site</span>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <div className="mb-5 rounded-2xl border border-border bg-card px-4 py-3 text-sm">
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Site</span>{" "}
          <span className="font-semibold">{scopeLabel}</span>
        </div>
      )}

      <Section title="Command Center">
        <div className="grid grid-cols-2 gap-3">
          <Stat to="/sites" state={originState} label="Active Sites" value={c.sites} icon={Building2} tone="default" />
          <Stat onClick={() => setScope("onDuty")} label="Guards on Duty" value={c.onDuty} icon={Users} tone="success" />
          <Stat onClick={() => setScope("openShifts")} label="Open Shifts" value={c.openShifts} icon={Briefcase} tone="warning" />
          <Stat onClick={() => setScope("incidents")} label="Incidents / Alerts" value={c.alerts} icon={AlertTriangle} tone="warning" />
          <Stat to="/operations/live" state={originState} label="On Site" value={live.summary.onPerimeter} icon={ShieldCheck} tone="success" />
          <Stat onClick={() => setScope("offPerimeter")} label="Off Perimeter" value={c.offPerimeter} icon={ShieldAlert} tone="danger" />
        </div>
      </Section>

      <Section title="">
        <RowCard
          icon={Bell}
          tone="danger"
          label="Open Alerts"
          value={c.openAlerts}
          actionLabel="View"
          to="/alerts"
          state={originState}
        />
        <RowCard
          icon={Repeat}
          tone="default"
          label="Shift Requests"
          value={c.shiftRequests > 0 ? `${c.shiftRequests} pending` : "All clear"}
          actionLabel="Review"
          to="/assignments"
          state={originState}
          asText
        />
      </Section>

      <Section title="Live Operations">
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <div className="relative">
            <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live Data
            </span>
            <PatrolMap
              geofences={live.fences}
              officers={live.officers}
              height={220}
              fitTo="geofences"
              intelligenceClassName="absolute left-3 top-12 z-[400] inline-flex min-h-9 items-center gap-1.5 rounded-md border border-[#C0C0C0]/40 bg-card/90 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#C0C0C0] backdrop-blur transition-colors hover:bg-[#222]"
            />
          </div>
          <LiveOpsStrip
            summary={live.summary}
            loading={live.loading}
            error={live.error}
            scopeLabel={scopeLabel}
          />
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="font-display text-base font-semibold">Live Map</div>
              <div className="text-xs text-muted-foreground">Real-time officer GPS · perimeter status</div>
            </div>
            <Link to="/operations/live" state={originState} className="inline-flex items-center gap-1 rounded-lg bg-foreground px-3 py-2 text-xs font-bold uppercase tracking-wider text-background">
              Open <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </Section>

      <Section title="Manage">
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <QuickRow to="/team" icon={Users} label="Personnel" state={originState} />
          <QuickRow to="/schedule" icon={Briefcase} label="Shifts" search={siteId !== "all" ? { site: siteId } : undefined} state={originState} />
          <QuickRow to="/sites" icon={MapPin} label="Sites" state={originState} />
          <QuickRow to="/geofences" icon={Hexagon} label="Sites & Geofences" state={originState} />
          <QuickRow to="/guard-tours" icon={QrCode} label="Guard Tours" state={originState} />
          <QuickRow to="/post-orders" icon={FileText} label="Post Orders" state={originState} />
          <QuickRow to="/team" icon={Mail} label="Invite Guards" state={originState} />
          <QuickRow to="/admin/audit" icon={ClipboardList} label="Audit Log" state={originState} />
          <QuickRow to="/security" icon={ShieldAlert} label="Security Center" state={originState} />
        </div>
      </Section>

      {currentOrg && (
        <SiteScopeSheet
          category={scope}
          orgId={currentOrg.id}
          siteIds={visibleSiteIds}
          scopeLabel={scopeLabel}
          originState={originState}
          onClose={() => setScope(null)}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      {title && <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{title}</h2>}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Stat({
  label, value, icon: Icon, tone, to, state, onClick,

}: { label: string; value: number; icon: any; tone: "default" | "success" | "warning" | "danger"; to?: string; state?: HistoryState; onClick?: () => void }) {
  const toneCls =
    tone === "success" ? "bg-success text-success-foreground" :
    tone === "warning" ? "bg-warning text-warning-foreground" :
    tone === "danger"  ? "bg-destructive text-destructive-foreground" :
                         "bg-foreground text-background";
  const body = (
    <>
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] leading-snug text-muted-foreground">{label}</span>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${toneCls}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold">{value}</div>
    </>
  );
  const cls = "block rounded-2xl border border-border bg-card p-4";
  if (onClick)
    return (
      <button type="button" onClick={onClick} className={`${cls} min-h-[44px] w-full text-left transition-colors hover:border-primary/50`}>
        {body}
      </button>
    );
  if (!to) return <div className={cls}>{body}</div>;
  return (
    <Link to={to as never} state={state} className={`${cls} transition-colors hover:border-primary/50 hover:bg-muted/30 active:bg-muted/50`}>
      {body}
    </Link>
  );
}

function RowCard({
  icon: Icon, label, value, tone, to, state, actionLabel, asText,
}: {
  icon: any; label: string; value: number | string; tone: "default" | "danger";
  to: string; state?: HistoryState; actionLabel: string; asText?: boolean;
}) {
  const toneCls = tone === "danger" ? "bg-destructive text-destructive-foreground" : "bg-foreground text-background";
  return (
    <Link to={to as never} state={state} className="flex items-center gap-4 rounded-2xl border border-border bg-card px-4 py-4 hover:bg-muted/30">
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${toneCls}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
        <div className={asText ? "font-display text-lg font-semibold" : "font-display text-2xl font-bold"}>{value}</div>
      </div>
      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
        {actionLabel} <ChevronRight className="h-4 w-4" />
      </span>
    </Link>
  );
}

function QuickRow({ to, icon: Icon, label, search, state }: { to: string; icon: any; label: string; search?: Record<string, unknown>; state?: HistoryState }) {
  return (
    <Link
      to={to as never}
      search={search as never}
      state={state}
      className="flex items-center justify-between gap-3 border-b border-border px-4 py-3.5 last:border-b-0 hover:bg-muted/40"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-base font-medium">{label}</span>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}
